import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import {
  User,
  Mail,
  Phone,
  Building2,
  Briefcase,
  Shield,
  Calendar,
  Clock,
  Hash,
  MapPin,
  Activity,
} from "lucide-react";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { cn } from "@/lib/utils";

interface UserDetailsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: {
    id: string;
    email: string;
    full_name: string | null;
    is_active: boolean;
    created_at: string;
    profiles?: {
      full_name: string | null;
      phone: string | null;
      department: string | null;
      position: string | null;
      employee_id: string | null;
      tag: string | null;
      area: string | null;
      hire_date: string | null;
      last_login: string | null;
      login_count: number;
      allowed_modules?: string[] | null;
      temporary_allowed_modules?: string[] | null;
      temporary_access_until?: string | null;
    };
    user_roles?: {
      role: string;
    }[];
  };
}

const MODULE_LABELS: Record<string, string> = {
  quotations: "Cotizaciones",
  "work-orders": "Ordenes de trabajo",
  processes: "Procesos",
  "quality-control": "Control de calidad",
  traceability: "Trazabilidad",
};

export function UserDetailsDialog({ open, onOpenChange, user }: UserDetailsDialogProps) {
  const role = user.user_roles?.[0]?.role || "viewer";
  const profile = user.profiles;

  const getRoleBadge = (role: string) => {
    const config: Record<string, { label: string; color: string }> = {
      admin: { label: "Administrador", color: "bg-red-500/10 text-red-500" },
      supervisor: { label: "Supervisor", color: "bg-purple-500/10 text-purple-500" },
      operator: { label: "Operador", color: "bg-blue-500/10 text-blue-500" },
      quality: { label: "Calidad", color: "bg-green-500/10 text-green-500" },
      warehouse: { label: "Almacén", color: "bg-orange-500/10 text-orange-500" },
      maintenance: { label: "Mantenimiento", color: "bg-cyan-500/10 text-cyan-500" },
      viewer: { label: "Visor", color: "bg-gray-500/10 text-gray-500" },
    };
    const { label, color } = config[role] || config.viewer;
    return <Badge className={color}>{label}</Badge>;
  };

  const formatDate = (date: string | null) => {
    if (!date) return "-";
    return format(new Date(date), "dd/MM/yyyy", { locale: es });
  };

  const formatDateTime = (date: string | null) => {
    if (!date) return "Nunca";
    return format(new Date(date), "dd/MM/yyyy HH:mm", { locale: es });
  };

  const formatModules = (modules?: string[] | null) => {
    if (!Array.isArray(modules)) return "Sin restricciones";
    if (modules.length === 0) return "Sin acceso";
    return modules.map((module) => MODULE_LABELS[module] || module).join(", ");
  };

  const formatTemporaryModules = (modules?: string[] | null, until?: string | null) => {
    if (!Array.isArray(modules) || modules.length === 0) return "Sin accesos temporales";
    const untilLabel = until ? formatDateTime(until) : "Sin fecha";
    return `${formatModules(modules)} (hasta ${untilLabel})`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="w-5 h-5" />
            Detalles del Usuario
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Header con avatar y info principal */}
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <span className="text-2xl font-bold text-primary">
                {(profile?.full_name || user.email)?.[0]?.toUpperCase()}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-xl font-semibold truncate">
                {profile?.full_name || user.full_name || "Sin nombre"}
              </h3>
              <p className="text-muted-foreground">{user.email}</p>
              <div className="flex items-center gap-2 mt-2">
                {getRoleBadge(role)}
                <Badge
                  className={cn(
                    user.is_active
                      ? "bg-green-500/10 text-green-500"
                      : "bg-gray-500/10 text-gray-500"
                  )}
                >
                  {user.is_active ? "Activo" : "Inactivo"}
                </Badge>
              </div>
            </div>
          </div>

          <Separator />

          {/* Información de contacto */}
          <div>
            <h4 className="font-medium mb-3 flex items-center gap-2">
              <Mail className="w-4 h-4" />
              Información de Contacto
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                    <Mail className="w-3 h-3" />
                    Email
                  </div>
                  <p className="font-medium truncate">{user.email}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                    <Phone className="w-3 h-3" />
                    Teléfono
                  </div>
                  <p className="font-medium">{profile?.phone || "-"}</p>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Información laboral */}
          <div>
            <h4 className="font-medium mb-3 flex items-center gap-2">
              <Briefcase className="w-4 h-4" />
              Información Laboral
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                    <Hash className="w-3 h-3" />
                    ID Empleado
                  </div>
                  <p className="font-medium">{profile?.employee_id || "-"}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                    <Hash className="w-3 h-3" />
                    Firma/Tag
                  </div>
                  <p className="font-medium">{profile?.tag || "-"}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                    <Building2 className="w-3 h-3" />
                    Departamento
                  </div>
                  <p className="font-medium capitalize">{profile?.department || "-"}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                    <Briefcase className="w-3 h-3" />
                    Puesto
                  </div>
                  <p className="font-medium">{profile?.position || "-"}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                    <MapPin className="w-3 h-3" />
                    Área
                  </div>
                  <p className="font-medium capitalize">{profile?.area || "-"}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                    <Shield className="w-3 h-3" />
                    Accesos
                  </div>
                  <p className="font-medium">{formatModules(profile?.allowed_modules)}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                    <Shield className="w-3 h-3" />
                    Acceso temporal
                  </div>
                  <p className="font-medium">
                    {formatTemporaryModules(profile?.temporary_allowed_modules, profile?.temporary_access_until || null)}
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Información de actividad */}
          <div>
            <h4 className="font-medium mb-3 flex items-center gap-2">
              <Activity className="w-4 h-4" />
              Actividad
            </h4>
            <div className="grid grid-cols-3 gap-4">
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                    <Calendar className="w-3 h-3" />
                    Fecha de Alta
                  </div>
                  <p className="font-medium">{formatDate(profile?.hire_date || null)}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                    <Clock className="w-3 h-3" />
                    Último Acceso
                  </div>
                  <p className="font-medium text-sm">{formatDateTime(profile?.last_login || null)}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
                    <Activity className="w-3 h-3" />
                    Total Accesos
                  </div>
                  <p className="font-medium">{profile?.login_count || 0}</p>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Fechas del sistema */}
          <div className="text-xs text-muted-foreground text-center pt-2 border-t">
            Usuario creado el {formatDateTime(user.created_at)}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}





